// Clarity integration utilities for capturing session and user IDs

// Extend Window interface for Microsoft Clarity
declare global {
  interface Window {
    clarity?: (action: string, ...args: any[]) => void;
    _clarity?: {
      _s: string; // Session ID
      _u: string; // User ID
    };
  }
}

export interface ClarityData {
  sessionId: string | null;
  userId: string | null;
  timestamp: string;
}

/**
 * Get Clarity session and user IDs from Microsoft Clarity
 */
export function getClarityData(): ClarityData {
  let sessionId: string | null = null;
  let userId: string | null = null;

  try {
    // Extract from Clarity cookies (this is the reliable method)
    const cookies = document.cookie.split(';');
    
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name && value) {
        // _clck cookie contains the user ID
        if (name === '_clck') {
          const decodedValue = decodeURIComponent(value);
          const parts = decodedValue.split('^');
          if (parts.length > 0) {
            userId = parts[0];
          }
        }
        
        // _clsk cookie contains the session ID
        if (name === '_clsk') {
          const decodedValue = decodeURIComponent(value);
          const parts = decodedValue.split('^');
          if (parts.length > 0) {
            sessionId = parts[0];
          }
        }
      }
    }

  } catch (error) {
    // silently ignore
  }

  return {
    sessionId,
    userId,
    timestamp: new Date().toISOString()
  };
}

/**
 * Set Clarity user ID using the identify method
 */
export function setClarityUserId(userId: string): void {
  try {
    if (window.clarity) {
      window.clarity('identify', userId);
    }
  } catch (_) {
    // silently ignore
  }
}

/**
 * Get Clarity headers for API requests
 */
export function getClarityHeaders(): Record<string, string> {
  const clarityData = getClarityData();
  const headers: Record<string, string> = {};

  if (clarityData.sessionId) {
    headers['clarity-session-id'] = clarityData.sessionId;
  }

  if (clarityData.userId) {
    headers['clarity-user-id'] = clarityData.userId;
  }

  return headers;
}

/**
 * Initialize Clarity tracking (delay to allow script load)
 */
export function initializeClarityTracking(): void {
  try {
    setTimeout(() => {
      // no-op
    }, 2000);
  } catch (_) {
    // silently ignore
  }
}
